import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { ToastProvider } from './context/ToastContext';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { CalendarPage } from './pages/CalendarPage';
import { RoomsListPage } from './pages/RoomsListPage';
import { MyBookingsPage } from './pages/MyBookingsPage';
import { UsersPage } from './pages/UsersPage';
import { AdminRoomsPage } from './pages/AdminRoomsPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { SetupPage } from './pages/SetupPage';
import { SettingsPage } from './pages/SettingsPage';
import { ParksPage } from './pages/ParksPage';
import { DevicesPage } from './pages/DevicesPage';
import { StatisticsPage } from './pages/StatisticsPage';
import { UserSettingsPage } from './pages/UserSettingsPage';
import { ReceptionistPage } from './pages/ReceptionistPage';
import { LdapConfigPage } from './pages/LdapConfigPage';
import { SsoCallbackPage } from './pages/SsoCallbackPage';
import { SsoConfigPage } from './pages/SsoConfigPage';
import { CompleteInvitePage } from './pages/CompleteInvitePage';
import { AdminDesksPage } from './pages/AdminDesksPage';
import { DesksPage } from './pages/DesksPage';
import { CompanySetupPage } from './pages/CompanySetupPage';
import { api } from './services/api';
import { UserRole } from './types';
import './styles.css';

function PrivateRoute({ children, adminOnly = false, companyAdminOnly = false, superAdminOnly = false, receptionistOnly = false }: {
  children: React.ReactNode;
  adminOnly?: boolean;
  companyAdminOnly?: boolean;
  superAdminOnly?: boolean;
  receptionistOnly?: boolean;
}) {
  const { user, loading, isAdmin, isCompanyAdmin, isSuperAdmin, isReceptionist } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" />;
  }

  if (companyAdminOnly && !isCompanyAdmin) {
    return <Navigate to="/" />;
  }

  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/" />;
  }

  if (receptionistOnly && !isReceptionist) {
    return <Navigate to="/" />;
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const [companySetupPending, setCompanySetupPending] = useState(false);
  const checkedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user || user.role !== UserRole.COMPANY_ADMIN) return;
    if (checkedUserRef.current === user.id) return;
    checkedUserRef.current = user.id;
    api.getCompany(user.companyId)
      .then(company => { if (company.setupPending) setCompanySetupPending(true); })
      .catch(() => {});
  }, [user]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (companySetupPending) {
    return (
      <CompanySetupPage
        onComplete={() => {
          checkedUserRef.current = null;
          setCompanySetupPending(false);
        }}
      />
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/" /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <CalendarPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/rooms"
        element={
          <PrivateRoute>
            <RoomsListPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-bookings"
        element={
          <PrivateRoute>
            <MyBookingsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/desks"
        element={
          <PrivateRoute>
            <DesksPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/account/settings"
        element={
          <PrivateRoute>
            <UserSettingsPage />
          </PrivateRoute>
        }
      />
      {/* Backward-compat redirect: old /account/security links go to the Security tab */}
      <Route
        path="/account/security"
        element={<Navigate to="/account/settings?tab=security" replace />}
      />
      <Route
        path="/users"
        element={
          <PrivateRoute companyAdminOnly>
            <UsersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/rooms"
        element={
          <PrivateRoute adminOnly>
            <AdminRoomsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/desks"
        element={
          <PrivateRoute adminOnly>
            <AdminDesksPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <PrivateRoute adminOnly>
            <CompaniesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <PrivateRoute adminOnly>
            <SettingsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/parks"
        element={
          <PrivateRoute superAdminOnly>
            <ParksPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/devices"
        element={
          <PrivateRoute adminOnly>
            <DevicesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/statistics"
        element={
          <PrivateRoute adminOnly>
            <StatisticsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/ldap/:companyId"
        element={
          <PrivateRoute companyAdminOnly>
            <LdapConfigPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/sso/:companyId"
        element={
          <PrivateRoute companyAdminOnly>
            <SsoConfigPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/reception"
        element={
          <PrivateRoute receptionistOnly>
            <ReceptionistPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/sso/callback"
        element={<SsoCallbackPage />}
      />
      <Route
        path="/invite/:token"
        element={<CompleteInvitePage />}
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function AppWithSetup() {
  const [setupStatus, setSetupStatus] = useState<'loading' | 'needed' | 'complete'>('loading');

  useEffect(() => {
    checkSetup();
  }, []);

  const checkSetup = async () => {
    try {
      const status = await api.checkSetupStatus();
      setSetupStatus(status.isSetup ? 'complete' : 'needed');
    } catch (error) {
      console.error('Failed to check setup status:', error);
      // If we can't check, assume setup is needed
      setSetupStatus('needed');
    }
  };

  if (setupStatus === 'loading') {
    return <div className="loading">Loading...</div>;
  }

  if (setupStatus === 'needed') {
    return <SetupPage onSetupComplete={() => setSetupStatus('complete')} />;
  }

  return (
    <AuthProvider>
      <SettingsProvider>
        <ConfirmProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </ConfirmProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppWithSetup />
    </BrowserRouter>
  );
}

export default App;
